import React from 'react'
import {cn} from './cn'

type BadgeVariant = 'default' | 'secondary' | 'success' | 'primary' | 'warning' | 'error' | 'info' | 'rose'
type BadgeSize = 'micro' | 'pill' | 'small' | 'medium' | 'large'

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
    variant?: BadgeVariant
    size?: BadgeSize
    children: React.ReactNode
    className?: string
}

const badgeVariants = {
    default: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    secondary: 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900/30 dark:text-slate-300 dark:border-gray-700',
    success: 'bg-success-50 text-success-700 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
    primary: 'bg-primary-50 text-primary-700 border-primary-200 dark:bg-primary-900/20 dark:text-primary-300 dark:border-primary-800',
    warning: 'bg-warning-50 text-warning-700 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
    error: 'bg-error-50 text-error-700 border-error-200 dark:bg-error-900/20 dark:text-error-300 dark:border-error-800',
    info: 'bg-primary-50 text-primary-700 border-primary-200 dark:bg-primary-900/20 dark:text-primary-300 dark:border-primary-800',
    rose: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-900/25 dark:text-fuchsia-200 dark:border-fuchsia-900/50'
} as const

const badgeSizes = {
    micro: 'px-1.5 py-0.5 text-[10px] font-medium',
    pill: 'px-2 py-0.5 text-[10px] font-semibold',
    small: 'px-2 py-0.5 text-xs font-medium',
    medium: 'px-2.5 py-1 text-sm font-medium',
    large: 'px-3 py-1.5 text-sm font-semibold'
} as const

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
    ({variant = 'default', size = 'medium', children, className, ...props}, ref) => {
        return (
            <span
                ref={ref}
                className={cn(
                    'inline-flex items-center justify-center',
                    'rounded-md border',
                    'whitespace-nowrap',
                    'transition-colors duration-150',
                    badgeSizes[size],
                    badgeVariants[variant],
                    className
                )}
                {...props}
            >
        {children}
      </span>
        )
    }
)

Badge.displayName = 'Badge'

interface RoleBadgeProps extends Omit<BadgeProps, 'variant'> {
    role: 'Manager' | 'Customer' | 'Parent' | 'Instructor' | string
}

const roleVariantMap: Record<string, BadgeVariant> = {
    'Manager': 'primary',
    'Customer': 'success',
    'Parent': 'info',
    'Instructor': 'warning'
}

const RoleBadge: React.FC<RoleBadgeProps> = ({role, children, ...props}) => {
    const variant = roleVariantMap[role] || 'default'

    return (
        <Badge variant={variant} {...props}>
            {children || role}
        </Badge>
    )
}

RoleBadge.displayName = 'RoleBadge'
